import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitize';

const TermsConditionsManager = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const fetchTermsConditions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/terms-conditions`);
      if (response.ok) {
        const data = await response.json();
        setContent(data.content);
        setLastUpdated(data.updated_at);
        setUpdatedBy(data.updated_by);
      }
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
      setMessage({ type: 'error', text: 'Failed to load terms and conditions' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/terms-conditions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });

      if (response.ok) {
        const data = await response.json();
        setLastUpdated(data.updated_at);
        setUpdatedBy(data.updated_by);
        setMessage({ type: 'success', text: 'Terms and conditions updated successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to update terms and conditions');
      }
    } catch (error) {
      console.error('Error updating terms and conditions:', error);
      setMessage({ type: 'error', text: 'Failed to update terms and conditions' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Terms and Conditions Manager</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Edit and manage the terms and conditions content</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {message.text && (
        <div className={`flex items-center gap-3 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {lastUpdated && (
        <div className="bg-blue-50 border border dark:border-gray-700-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Last updated:</strong> {new Date(lastUpdated).toLocaleString()}
            {updatedBy && ` by ${updatedBy}`}
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Terms and Conditions Content
        </label>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Enter terms and conditions content here..."
        />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Use the toolbar above to format text (bold, italic, headings, lists, etc.)
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preview</h3>
        <div className="border border dark:border-gray-700-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) || '<p class="text-gray-500 dark:text-gray-400">No content to preview</p>' }}
          />
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsManager;
